/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ElementRef,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as momentLib from 'moment';
import { Moment } from 'moment';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FormControlsService } from '../services/form-controls.service';

const moment = (momentLib as any).default || momentLib;

@Component({
  standalone: false,
  selector: 'lib-cm-datetimepicker',
  templateUrl: './cm-datetimepicker.component.html',
  styleUrls: ['./cm-datetimepicker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmDateTimepickerComponent),
      multi: true,
    },
  ],
})
export class CmDateTimepickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  val!: Moment;

  isOpen = false;
  private overlayRef?: OverlayRef;

  calendarSelected: Moment | null = null;
  hours = 12;
  minutes = 0;
  isPM = false;

  @ViewChild('panel') panelTemplate!: TemplateRef<any>;
  @ViewChild('wrapper') wrapperRef!: ElementRef;

  @Input() options: any;
  @Input() defaultTime = [0, 0, 0];
  @Input() disabled!: boolean;
  @Input() placeholder = 'Choose Date';
  @Input() enableMeridian = true;
  @Input() minDate: Date | null = null;

  @Output() dateChange = new EventEmitter<{ value: Moment | null }>();

  onChange!: (val: any) => void;
  onTouched!: (val: any) => void;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    public formControlsService: FormControlsService,
  ) {}

  get displayValue(): string {
    if (!this.val || !this.val.isValid()) return '';
    return this.val.format(this.enableMeridian ? 'MM/DD/YYYY, hh:mm A' : 'MM/DD/YYYY, HH:mm');
  }

  get minDateMoment(): Moment | null {
    return this.minDate ? moment(this.minDate) : null;
  }

  pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  open() {
    if (this.disabled || this.isOpen) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.wrapperRef)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.isOpen = true;
  }

  close() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
    this.isOpen = false;
  }

  onCalendarDateSelected(date: Moment) {
    this.calendarSelected = date;
  }

  confirm() {
    const base = this.calendarSelected || (this.val?.isValid() ? this.val.clone() : moment());
    const m = base.clone();

    let h = this.hours;
    if (this.enableMeridian) {
      if (this.isPM && h < 12) h += 12;
      if (!this.isPM && h === 12) h = 0;
    }
    m.hours(h).minutes(this.minutes).seconds(0).milliseconds(0);

    this.value = m;
    this.dateChange.emit({ value: m });
    this.close();
  }

  incrementHours() {
    this.hours = this.enableMeridian ? (this.hours % 12) + 1 : (this.hours + 1) % 24;
  }

  decrementHours() {
    this.hours = this.enableMeridian
      ? ((this.hours - 2 + 12) % 12) + 1
      : (this.hours - 1 + 24) % 24;
  }

  incrementMinutes() {
    this.minutes = (this.minutes + 1) % 60;
  }

  decrementMinutes() {
    this.minutes = (this.minutes - 1 + 60) % 60;
  }

  toggleAMPM() {
    this.isPM = !this.isPM;
  }

  set value(val: Moment) {
    if (val !== undefined && JSON.stringify(val) !== JSON.stringify(this.val)) {
      this.val = val;
      if (this.onChange) this.onChange(val);
      if (this.onTouched) this.onTouched(val);
    }
  }

  get value(): Moment {
    return this.val;
  }

  ngOnInit() {
    this.writeValue('false');
  }

  ngOnDestroy() {
    this.close();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    const m = moment(value);
    this.val = m;
    if (m.isValid()) {
      this.calendarSelected = m.clone();
      const h = m.hours();
      if (this.enableMeridian) {
        this.isPM = h >= 12;
        this.hours = h % 12 || 12;
      } else {
        this.hours = h;
      }
      this.minutes = m.minutes();
    } else {
      this.calendarSelected = null;
      this.hours = 12;
      this.minutes = 0;
      this.isPM = false;
    }
  }
}
